// Time helpers shared by api and web. Timestamps are ISO 8601 UTC strings (`...Z`), dates are
// `YYYY-MM-DD` in Europe/Berlin, minutes are the smallest unit.
import { TZDate } from "@date-fns/tz";
import { formatISO, isValid, parseISO } from "date-fns";
import type { Weekday } from "./enums.js";
import { WEEKDAYS } from "./enums.js";

export const TIME_ZONE = "Europe/Berlin";

export type IsoDate = string; // YYYY-MM-DD
export type IsoTimestamp = string; // 2026-09-18T06:00:00.000Z
export type IsoWeek = string; // 2026-W38

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const ISO_WEEK = /^(\d{4})-W(\d{2})$/;

export function isIsoDate(value: string): value is IsoDate {
  const match = ISO_DATE.exec(value);
  if (!match) return false;
  const parsed = new Date(`${value}T00:00:00Z`);
  return isValid(parsed) && parsed.toISOString().slice(0, 10) === value;
}

// A week id is valid when it round-trips: `2025-W53` and `2026-W00` are not weeks of those years.
export function isIsoWeek(value: string): value is IsoWeek {
  const match = ISO_WEEK.exec(value);
  if (!match) return false;
  const week = Number(match[2]);
  if (week < 1 || week > 53) return false;
  return isoWeekOf(isoWeekRange(value).start) === value;
}

// Drops seconds and milliseconds: the zvoove API truncates to the minute and so do we.
export function roundToMinute(date: Date): Date {
  const copy = new Date(date.getTime());
  copy.setUTCSeconds(0, 0);
  return copy;
}

export function toIsoTimestamp(date: Date): IsoTimestamp {
  return roundToMinute(date).toISOString();
}

export function parseTimestamp(value: string): Date | null {
  const parsed = parseISO(value);
  return isValid(parsed) ? parsed : null;
}

// Berlin calendar date of an instant.
export function toBerlinDate(date: Date): IsoDate {
  return formatISO(new TZDate(date, TIME_ZONE), { representation: "date" });
}

// Berlin wall-clock "HH:mm" of an instant.
export function toBerlinTime(date: Date): string {
  const local = new TZDate(date, TIME_ZONE);
  return `${String(local.getHours()).padStart(2, "0")}:${String(local.getMinutes()).padStart(2, "0")}`;
}

// Instant of a Berlin wall-clock time on a date. Ambiguous times of the DST switch resolve to
// the first occurrence, non-existent times to the hour after the gap (TZDate semantics).
export function berlinToUtc(date: IsoDate, time: string): Date {
  const [y, m, d] = date.split("-").map(Number) as [number, number, number];
  const [hh, mm] = time.split(":").map(Number) as [number, number];
  return new Date(new TZDate(y, m - 1, d, hh, mm, 0, 0, TIME_ZONE).getTime());
}

// zvoove's API timestamps are Berlin wall-clock values: it converts an offset it receives to UTC
// and then shows and returns that value as the local time (verified in zvoove One on
// 2026-10-09: `08:00:00+02:00` became 06:00). So the app sends wall-clock without an offset
// (`2026-10-05T08:00:00`) and reads the returned `…Z` values as wall-clock again.
export function toZvooveTimestamp(date: Date): string {
  const local = new TZDate(roundToMinute(date), TIME_ZONE);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${local.getFullYear()}-${pad(local.getMonth() + 1)}-${pad(local.getDate())}T${pad(local.getHours())}:${pad(local.getMinutes())}:00`;
}

// Instant of a zvoove timestamp (`2026-10-05T08:00:00Z` or without `Z`): the wall-clock part is
// Berlin time, the `Z` is nominal.
export function fromZvooveTimestamp(value: string): Date {
  const m = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/.exec(value);
  if (!m) return new Date(Number.NaN);
  return berlinToUtc(m[1] as IsoDate, m[2] as string);
}

// Calendar arithmetic on dates runs in UTC (midnight UTC instants) so neither DST nor the host
// timezone can shift a day.
const DAY_MS = 86_400_000;

export function parseIsoDate(date: IsoDate): Date {
  if (!isIsoDate(date)) throw new RangeError(`Invalid ISO date: ${date}`);
  return new Date(`${date}T00:00:00Z`);
}

// UTC calendar date of an instant (use `toBerlinDate` for wall-clock dates).
export function formatIsoDate(date: Date): IsoDate {
  return date.toISOString().slice(0, 10);
}

export function addDaysIso(date: IsoDate, days: number): IsoDate {
  return formatIsoDate(new Date(parseIsoDate(date).getTime() + days * DAY_MS));
}

export function daysBetween(from: IsoDate, to: IsoDate): number {
  return Math.round((parseIsoDate(to).getTime() - parseIsoDate(from).getTime()) / DAY_MS);
}

// Every date of an inclusive range, in order.
export function eachDateOfRange(from: IsoDate, to: IsoDate): IsoDate[] {
  const out: IsoDate[] = [];
  const total = daysBetween(from, to);
  for (let i = 0; i <= total; i += 1) out.push(addDaysIso(from, i));
  return out;
}

// 0 = Monday … 6 = Sunday.
export function weekdayIndex(date: IsoDate): number {
  return (parseIsoDate(date).getUTCDay() + 6) % 7;
}

export function weekdayOf(date: IsoDate): Weekday {
  return WEEKDAYS[weekdayIndex(date)] as Weekday;
}

// Monday of the ISO week containing a UTC-midnight instant.
function mondayOf(d: Date): Date {
  return new Date(d.getTime() - ((d.getUTCDay() + 6) % 7) * DAY_MS);
}

export function isoWeekOf(date: IsoDate): IsoWeek {
  // The Thursday of the week decides the ISO year; 4 January is always in week 1.
  const thursday = new Date(mondayOf(parseIsoDate(date)).getTime() + 3 * DAY_MS);
  const year = thursday.getUTCFullYear();
  const week1Monday = mondayOf(new Date(Date.UTC(year, 0, 4)));
  const weekNo = Math.round((thursday.getTime() - week1Monday.getTime()) / (7 * DAY_MS)) + 1;
  return `${year}-W${String(weekNo).padStart(2, "0")}`;
}

// Monday and Sunday (inclusive) of an ISO week.
export function isoWeekRange(week: IsoWeek): { start: IsoDate; end: IsoDate } {
  const match = ISO_WEEK.exec(week);
  if (!match) throw new RangeError(`Invalid ISO week: ${week}`);
  const year = Number(match[1]);
  const weekNo = Number(match[2]);
  const monday = new Date(
    mondayOf(new Date(Date.UTC(year, 0, 4))).getTime() + (weekNo - 1) * 7 * DAY_MS,
  );
  return {
    start: formatIsoDate(monday),
    end: formatIsoDate(new Date(monday.getTime() + 6 * DAY_MS)),
  };
}

export function shiftIsoWeek(week: IsoWeek, weeks: number): IsoWeek {
  const { start } = isoWeekRange(week);
  return isoWeekOf(addDaysIso(start, weeks * 7));
}

export function minutesBetween(start: Date, end: Date): number {
  return Math.round((roundToMinute(end).getTime() - roundToMinute(start).getTime()) / 60_000);
}

// Minutes → "7,50 h" style number with two decimals (German formatting is the web's job).
export function minutesToHours(minutes: number): number {
  return Math.round((minutes / 60) * 100) / 100;
}

// "8:30" from minutes; hours without leading zero, minutes padded.
export function formatHours(minutes: number): string {
  const sign = minutes < 0 ? "-" : "";
  const abs = Math.abs(minutes);
  return `${sign}${Math.floor(abs / 60)}:${String(abs % 60).padStart(2, "0")}`;
}

export function hoursToMinutes(hours: number): number {
  return Math.round(hours * 60);
}
